import { useState } from "react";
import { ProductRelation } from "./CatalogProductForm";
import { buttonClass, inputClass, toDateTimeInput } from "./catalogUtils";

export const LISTINGS_ENDPOINT = "/api/superadmin/catalog/supplier-listings/";
const OPTIONS_ENDPOINT = `${LISTINGS_ENDPOINT}options/`;

function estimatePrices({ trade_price: tradePrice, discount, gst_rate: gstRate, price_includes_gst: includesGst }) {
  const trade = Number(tradePrice);
  if (tradePrice === "" || Number.isNaN(trade)) return null;
  const net = trade * (1 - (Number(discount) || 0) / 100);
  const gstFactor = 1 + (Number(gstRate) || 0) / 100;
  const excluding = includesGst ? net / gstFactor : net;
  const including = includesGst ? net : net * gstFactor;
  return { net: net.toFixed(2), excluding: excluding.toFixed(2), including: including.toFixed(2) };
}

export default function SupplierListingForm({ listing, busy, error, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    vendor: listing?.vendor ? String(listing.vendor) : "",
    vendor_name: listing?.vendor_name ?? "",
    product: listing?.product ? String(listing.product) : "",
    product_name: listing ? `${listing.product_detail.name} (${listing.product_detail.sku})` : "",
    trade_price: listing?.trade_price ?? "",
    discount: listing?.discount ?? "0",
    gst_rate: listing?.gst_rate ?? "18",
    price_includes_gst: listing?.price_includes_gst ?? false,
    min_order_quantity: listing?.min_order_quantity ?? "",
    valid_from: toDateTimeInput(listing?.valid_from),
    valid_until: toDateTimeInput(listing?.valid_until),
    is_active: listing?.is_active ?? true,
    note: "",
  }));
  const [dateError, setDateError] = useState("");
  const preview = estimatePrices(form);

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }
  function submit(event) {
    event.preventDefault();
    if (form.valid_until <= form.valid_from) {
      setDateError("Valid until must be after valid from.");
      return;
    }
    setDateError("");
    onSave({
      ...(listing ? {} : { vendor: Number(form.vendor), product: Number(form.product) }),
      trade_price: form.trade_price,
      discount: form.discount === "" ? "0" : form.discount,
      gst_rate: form.gst_rate === "" ? "0" : form.gst_rate,
      price_includes_gst: form.price_includes_gst,
      min_order_quantity: form.min_order_quantity === "" ? null : form.min_order_quantity,
      valid_from: form.valid_from,
      valid_until: form.valid_until,
      is_active: form.is_active,
      note: form.note.trim(),
    });
  }

  return (
    <form aria-label={listing ? "Edit supplier listing" : "Create supplier listing"} onSubmit={submit}>
      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <fieldset disabled={busy} className="grid min-w-0 gap-4 sm:grid-cols-2">
        {listing ? (
          <div className="rounded-lg bg-slate-50 p-3 text-sm sm:col-span-2">
            <p className="font-medium text-slate-900">{form.product_name}</p>
            <p className="mt-1 text-slate-500">Supplier: {form.vendor_name}</p>
          </div>
        ) : (
          [["vendor", "Supplier"], ["product", "Product"]].map(([kind, label]) => (
            <ProductRelation key={kind} kind={kind} label={label} required idPrefix="listing"
              endpoint={OPTIONS_ENDPOINT} value={form[kind]} selectedName={form[`${kind}_name`]} disabled={busy}
              onChange={(value, name) => setForm((current) => ({ ...current, [kind]: value, [`${kind}_name`]: name }))} />
          ))
        )}
        {[
          ["trade_price", "Trade price (₹)", "0", "0.01", "999999999999.99", true],
          ["discount", "Discount (%)", "0", "0.01", "100", false],
          ["gst_rate", "GST rate (%)", "0", "0.01", "100", false],
          ["min_order_quantity", "Minimum order quantity (optional)", "0.001", "0.001", "999999999.999", false],
        ].map(([field, label, min, step, max, required], index) => (
          <div key={field}>
            <label htmlFor={`listing-${field}`} className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
            <input id={`listing-${field}`} type="number" min={min} max={max} step={step} required={required}
              data-initial-focus={listing && index === 0 ? true : undefined}
              value={form[field]} onChange={(event) => change(field, event.target.value)} className={inputClass} />
          </div>
        ))}
        <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
          <input type="checkbox" checked={form.price_includes_gst}
            onChange={(event) => change("price_includes_gst", event.target.checked)} />
          Trade price already includes GST
        </label>
        {[["valid_from", "Valid from"], ["valid_until", "Valid until"]].map(([field, label]) => (
          <div key={field}>
            <label htmlFor={`listing-${field}`} className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
            <input id={`listing-${field}`} type="datetime-local" required value={form[field]}
              aria-invalid={field === "valid_until" && Boolean(dateError)}
              onChange={(event) => { change(field, event.target.value); setDateError(""); }} className={inputClass} />
          </div>
        ))}
        {dateError && <p role="alert" className="text-sm text-red-600 sm:col-span-2">{dateError}</p>}
        <div className="sm:col-span-2">
          <label htmlFor="listing-note" className="mb-1 block text-sm font-medium text-slate-700">Change note (optional)</label>
          <input id="listing-note" maxLength={255} placeholder="Saved in price history, e.g. agreed on call"
            value={form.note} onChange={(event) => change("note", event.target.value)} className={inputClass} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active} onChange={(event) => change("is_active", event.target.checked)} />
          Active
        </label>
      </fieldset>
      {preview && (
        <dl aria-label="Estimated price" className="mt-4 grid grid-cols-3 gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
          {[["After discount", preview.net], ["Excl. GST", preview.excluding], ["Incl. GST", preview.including]].map(([label, value]) => (
            <div key={label}><dt className="text-xs text-slate-500">{label}</dt><dd className="font-semibold text-slate-900">₹{value}</dd></div>
          ))}
        </dl>
      )}
      <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="submit"
          disabled={busy || form.trade_price === "" || !form.valid_from || !form.valid_until || (!listing && (!form.vendor || !form.product))}
          className={`${buttonClass} bg-blue-500 text-white hover:bg-blue-600`}>{busy ? "Saving..." : "Save Listing"}</button>
        <button type="button" disabled={busy} onClick={onClose}
          className={`${buttonClass} border border-slate-200 text-slate-600 hover:bg-slate-50`}>Cancel</button>
      </div>
    </form>
  );
}
